import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { useStackNavigate } from '@/app/navigation/stack-links';
import { useWorkorders } from '@/features/workorders/workorders.hooks';
import { WorkorderTable } from '@/features/workorders/workorders.table';
import { WorkordersMapProvider } from '@/features/workorders/workorders.context';
import { WorkordersMap } from '@/features/workorders/components/WorkordersMap';
import { MapViewToggle } from '@/features/workorders/components/MapViewToggle';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { cn } from '@/lib/utils';
import { useCallback, useMemo, useState } from 'react';
import { subMonths } from 'date-fns';
import { Workorder } from '@certhon/models';

export const WorkordersPanel = ({
  params,
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const dateStr = subMonths(new Date(), 3).toISOString().split('T')[0];
  const resource = useWorkorders({
    or: `(progress.eq.scheduled,and(progress.eq.completed,execution_date.gt.${dateStr}))`,
    order: 'recnum.desc',
  });

  const navigate = useStackNavigate();
  const [showMap, setShowMap] = useLocalStorage('workorders.showMap', false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [filteredWorkorders, setFilteredWorkorders] = useState<Workorder[]>([]);

  // Wrapper to handle both string and number IDs from DataTable
  const handleRowHover = (id: string | number | null) => {
    if (typeof id === 'number' || id === null) {
      setHoveredId(id);
    }
  };

  // Callback to sync filtered data from table to map
  const handleFilteredDataChange = useCallback((data: Workorder[]) => {
    setFilteredWorkorders(data);
  }, []);

  // Filter workorders with valid addresses for map display (from filtered data)
  const workordersWithLocation = useMemo(
    () => filteredWorkorders.filter((wo) => wo.location_address),
    [filteredWorkorders],
  );

  return (
    <Panel {...panelProps}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Workorders</h2>
        <MapViewToggle showMap={showMap} onToggleMap={setShowMap} />
      </div>

      <WorkordersMapProvider>
        <div
          className={cn(
            'flex flex-col',
            showMap && '@lg:grid @lg:grid-cols-2 @lg:gap-4',
          )}
        >
          <WorkorderTable
            {...resource}
            hoveredId={hoveredId}
            onRowHover={handleRowHover}
            onFilteredDataChange={handleFilteredDataChange}
          />

          {showMap && (
            <div
              className={cn(
                'order-first @lg:order-last',
                '@lg:sticky @lg:top-0 @lg:self-start',
              )}
            >
              <WorkordersMap
                workorders={workordersWithLocation}
                hoveredId={hoveredId}
                onPinHover={setHoveredId}
                onPinClick={(wo) =>
                  navigate({ to: `/workorders/${wo.recnum}` })
                }
                className="h-64 rounded-lg border @lg:h-[calc(100vh-12rem)] @lg:min-h-[400px]"
              />
            </div>
          )}
        </div>
      </WorkordersMapProvider>
    </Panel>
  );
};
